import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Container } from '../../UI/Containers';
import Table from '../../UI/Table';
import { Button } from '../../UI/Buttons';
import attendanceService from '../../../Services/Api/attendanceService';

const ViewContainer = styled(Container)`
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const Controls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const DateInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  min-width: 200px;
`;

function getCycleRange(date = new Date()) {
  const d = new Date(date);
  const day = d.getDate();
  let start, end;

  if (day >= 25) {
    start = new Date(d.getFullYear(), d.getMonth(), 25);
    end = new Date(d.getFullYear(), d.getMonth() + 1, 24);
  } else {
    start = new Date(d.getFullYear(), d.getMonth() - 1, 25);
    end = new Date(d.getFullYear(), d.getMonth(), 24);
  }

  const fmt = (dt) => dt.toISOString().split('T')[0];
  return { start_date: fmt(start), end_date: fmt(end) };
}

function AnalyticsView() {
  const defaultRange = getCycleRange(new Date());
  const [startDate, setStartDate] = useState(defaultRange.start_date);
  const [endDate, setEndDate] = useState(defaultRange.end_date);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [perUser, setPerUser] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [topEmployee, setTopEmployee] = useState(null);
  const [totalWorkingDays, setTotalWorkingDays] = useState(0);
  const [lateTrend, setLateTrend] = useState([]);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const loadChartJS = () => new Promise((resolve, reject) => {
    if (window.Chart) return resolve(window.Chart);
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/chart.js@3.9.1/dist/chart.min.js';
    s.onload = () => resolve(window.Chart);
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });

  const columns = [
    { key: 'username', label: 'Username' },
    { key: 'name', label: 'Name' },
    { key: 'total_working_days', label: 'Working Days', align: 'center' },
    { key: 'days_present', label: 'Days Present', align: 'center' },
    { key: 'leave_days', label: 'Leave Days', align: 'center' },
    { key: 'late_count', label: 'Late Count', align: 'center' },
    { key: 'overtime_hours', label: 'Overtime (hrs)', align: 'center' },
    { key: 'attendance_percentage', label: 'Attendance %', align: 'center' }
  ];

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await attendanceService.getAttendanceAnalytics(startDate, endDate);
      setTotalWorkingDays(res.total_working_days || 0);
    setLateTrend(res.late_trend || []);
      setPerUser(Array.isArray(res.per_user) ? res.per_user.map((p) => ({
        id: p.user.id,
        username: p.user.username,
        name: `${p.user.first_name || ''} ${p.user.last_name || ''}`.trim(),
        total_working_days: p.total_working_days,
        days_present: p.days_present,
        leave_days: p.leave_days,
        late_count: p.late_count,
        overtime_hours: p.overtime_hours,
        attendance_percentage: p.attendance_percentage
      })) : []);
      setDepartments(res.departments || []);
      setTopEmployee(res.top_employee || null);
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    // render chart when lateTrend updates
    async function render() {
      if (!chartRef.current) return;
      try {
        const Chart = await loadChartJS();
        // destroy previous instance
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }

        const labels = lateTrend.map(t => t.date);
        const presentData = lateTrend.map(t => t.present_count);
        const latePctData = lateTrend.map(t => t.late_percentage);

        const ctx = chartRef.current.getContext('2d');
        chartInstanceRef.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: 'Present',
                data: presentData,
                backgroundColor: '#64b5f6',
                yAxisID: 'y'
              },
              {
                label: 'Late %',
                data: latePctData,
                backgroundColor: '#f06292',
                yAxisID: 'y1'
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                type: 'linear',
                position: 'left',
                beginAtZero: true,
                title: { display: true, text: 'Count' }
              },
              y1: {
                type: 'linear',
                position: 'right',
                beginAtZero: true,
                grid: { drawOnChartArea: false },
                ticks: { callback: (v) => `${v}%` },
                title: { display: true, text: 'Late %' }
              }
            }
          }
        });
      } catch (e) {
        // ignore chart render errors
        // console.error('Chart load error', e);
      }
    }
    if (lateTrend && lateTrend.length > 0) render();
    return () => { if (chartInstanceRef.current) { chartInstanceRef.current.destroy(); chartInstanceRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lateTrend]);

  return (
    <ViewContainer>
      <Controls>
        <label>
          From:&nbsp;
          <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label>
          To:&nbsp;
          <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>

        <Button onClick={fetchAnalytics} variant="primary">Fetch</Button>
      </Controls>

      <SummaryRow>
        <Card>
          <div><strong>Total Working Days</strong></div>
          <div>{totalWorkingDays}</div>
        </Card>

        <Card>
          <div><strong>Top Employee</strong></div>
          {topEmployee ? (
            <div>{topEmployee.user.first_name} {topEmployee.user.last_name} </div>
          ) : <div>—</div>}
        </Card>

        <Card>
          <div><strong>Departments</strong></div>
          <div>{departments.length}</div>
        </Card>
      </SummaryRow>

      <h3>Late Arrival Trend</h3>
      {lateTrend.length === 0 ? <div>No data</div> : (
        <div style={{ height: 360 }}>
          <canvas ref={chartRef} />
        </div>
      )}

      {/* <Table columns={columns} data={perUser} isLoading={isLoading} error={error} /> */}

      <h3>Department Summaries</h3>
      <Table
        columns={[
          { key: 'dep_name', label: 'Department' },
          { key: 'total_users', label: 'Users', align: 'center' },
          { key: 'avg_attendance_percentage', label: 'Avg Attendance %', align: 'center' },
          { key: 'total_leave_days', label: 'Total Leave Days', align: 'center' },
          { key: 'late_count', label: 'Late Count', align: 'center' },
          { key: 'avg_overtime_hours', label: 'Avg OT (hrs)', align: 'center' }
        ]}
        data={departments}
      />
    </ViewContainer>
  );
}

export default AnalyticsView;
